<template>
  <div class="input-item-wrap">
    <el-popover
      v-if="isEnterPopoverMode"
      :visible="enterPopoverVisible"
      :placement="enterAction.popoverPlacement || 'right'"
      :width="enterAction.popoverWidth || 400"
      :popper-class="enterAction.popupClass || ''"
      :popper-style="enterPopoverStyle"
      @update:visible="enterPopoverVisible = $event"
    >
      <template #reference>
        <el-input
          v-model="value"
          class="input-item"
          :type="config.componentType || 'text'"
          :placeholder="getLocaleText(config.placeholder || '請選擇')"
          @keyup.enter="handleEnter"
        >
          <template v-if="config.btnText" #append>
            <el-button @click="search">{{ getLocaleText(config.btnText) }}</el-button>
          </template>
        </el-input>
      </template>
      <PopoverPageRender
        :area-data="enterAction.popoverPageData"
        :width="enterAction.popoverWidth || 400"
        :background-color="enterAction.popoverBackgroundColor || '#ffffff'"
        :border-radius="getPopoverBorderRadius(enterAction)"
      />
    </el-popover>
    <el-input
      v-else
      v-model="value"
      class="input-item"
      :type="config.componentType || 'text'"
      :placeholder="getLocaleText(config.placeholder || '請選擇')"
      @keyup.enter="handleEnter"
    >
      <template  v-if="config.btnText" #append>
        <el-button @click="search">{{ getLocaleText(config.btnText) }}</el-button>
      </template>
    </el-input>

    <el-dialog
      v-if="enterAction.responseType === 'popup'"
      v-model="dialogTableVisible"
      :class="enterAction.popupClass || ''"
      :title="enterAction.popupTitle || ''"
      append-to-body
    >
      <template #default>
        <div
          v-if="dialogTableVisible && articleContent?.content"
          class="new-detail-item-cont"
          v-html="articleContent.content"
        ></div>
      </template>
      <template #footer>
        <el-button type="primary" @click="dialogTableVisible = false">
          {{ getLocaleText('关闭') }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>
  
  <script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed, inject } from "vue";
import useLocate from "./uses/useLocate.js";
import { useRenderConfig } from "./uses/useRenderConfig.js";
import { resolveBindDataKeyValue } from "@/utils/renderBinding";
import { getArticleDetails } from "@/api";
import { getSafeLink, isTarget_Blank } from "@/utils/globalData.js";
import PopoverPageRender from "@/components/render/PopoverPageRender.vue";
import { useLowCodeActionRuntime } from "@/composables/useLowCodeActionRuntime.js";
import { isLowCodePopupPageAction } from "@/utils/lowCodePopupPage.js";
import { hasMeaningfulLowCodeInteraction } from "@/utils/lowCodeInteraction.js";
import { trackLowCodeEvent } from "@/utils/lowCodeTracking.js";
// props 声明
const props = defineProps({
  currentElementIndex: {
    type: Array,
    default: () => []
  },
  idx: {
    type: Number,
    default: -1
  },
  parentKeys: {
    type: Array,
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: true
  },
  config: {
    type: Object
  }
});
let emit = defineEmits(["change"]);
// 响应式数据
const value = ref("");
let lastName = ref("");
const { bindData } = useRenderConfig(() => props.config);
const bindDefaultValue = computed(() => {
  return resolveBindDataKeyValue(
    bindData.value,
    props.config?.bindDataKey,
    "",
  );
});
const { dispatchLowCodeAction } = useLowCodeActionRuntime();
const getConfigJson = inject("getConfigJson", () => ({}));
const dialogTableVisible = ref(false);
const articleContent = ref(null);
const enterPopoverVisible = ref(false);
const enterAction = computed(() => props.config?.enterAction || {});
const hasEnterAction = computed(() =>
  hasMeaningfulLowCodeInteraction(enterAction.value)
);
const isEnterPopoverMode = computed(() =>
  hasEnterAction.value && enterAction.value.responseType === "popoverPage"
);
const enterPopoverStyle = computed(() => ({
  padding: "0px",
  border: "none",
  overflow: "hidden",
  borderRadius: `${getPopoverBorderRadius(enterAction.value)}px`,
  backgroundColor: enterAction.value.popoverBackgroundColor || "#ffffff",
}));

// 计算属性
const currentElementIndexStr = computed(() => {
  return props.currentElementIndex && props.currentElementIndex.length
    ? props.currentElementIndex.join("x")
    : "";
});

// watch 监听
watch(
  () => props.config,
  config => {
    if (config.name !== lastName.value) {
      if (!window.pageFields) {
        window.pageFields = {};
      }
      window.pageFields[config.name] = {
        setValue,
        getValue
      };
      lastName.value = config.name;
      // 清理旧的配置
      delete window.pageFields[lastName.value];
      delete window[`on${config.name}ValueChange`];
    }
  },
  { deep: true }
);

watch(value, v => {
  if (typeof window[`on${props.config.name}ValueChange`] === "function") {
    window[`on${props.config.name}ValueChange`](v);
  }
  if (!props.config.btnText) {
    emit("change", v);
  }
});

watch(
  bindDefaultValue,
  (nextValue) => {
    if (nextValue !== value.value) {
      value.value = nextValue;
    }
  },
  { immediate: true },
);

watch(
  () => enterAction.value?.article,
  () => getEnterArticleContent(),
  { deep: true, immediate: true },
);

// 生命周期钩子
onMounted(() => {
  if (typeof window !== "undefined") {
    if (!window.pageFields) {
      window.pageFields = {};
    }
    lastName.value = props.config.name;
    window.pageFields[props.config.name] = {
      setValue,
      getValue
    };
  }
});

onBeforeUnmount(() => {
  if (typeof window !== "undefined" && window.pageFields) {
    delete window.pageFields[props.config.name];
  }
  delete window[`on${props.config.name}ValueChange`];
});

// 方法
const setValue = v => {
  value.value = v;
};

const getValue = () => {
  return value.value;
};

const search = () => {
  if (typeof window[`on${props.config.name}ValueChange`] === "function") {
    window[`on${props.config.name}ValueChange`](value.value);
  }
  emit("change", value.value);
};

function getPopoverBorderRadius(config) {
  return typeof config?.popoverBorderRadius === "number"
    ? config.popoverBorderRadius
    : 6;
}

async function getEnterArticleContent() {
  if (
    enterAction.value?.responseType !== "popup" ||
    !enterAction.value?.article?.id
  ) {
    articleContent.value = null;
    return;
  }
  const res = await getArticleDetails({
    pageNo: 0,
    pageSize: 9999,
    id: enterAction.value.article.id,
    status: "1",
  });
  articleContent.value = res.data?.dataList?.[0] || null;
  if (articleContent.value?.type === 1 && articleContent.value.content) {
    articleContent.value.content = articleContent.value.content.replace(
      "video",
      "video autoplay ",
    );
  }
}

const handleEnter = (event) => {
  const action = enterAction.value;
  const page = typeof getConfigJson === "function" ? getConfigJson() : {};
  if (props.disabled) {
    trackLowCodeEvent(action.trackingConfig, {
      trigger: "enter",
      element: {
        ...(props.config || {}),
        value: value.value,
      },
      elementType: "inputItem",
      elementIndex: currentElementIndexStr.value,
      page,
      binding: page?.bindData || bindData.value || {},
      runtime: {
        inputValue: value.value,
      },
    });
  }
  if (!hasEnterAction.value) return;

  if (isLowCodePopupPageAction(action)) {
    event?.preventDefault?.();
    dispatchLowCodeAction(
      action,
      page,
    );
    return;
  }
  if (action.responseType === "popup") {
    event?.preventDefault?.();
    dialogTableVisible.value = true;
    return;
  }
  if (action.responseType === "popoverPage") {
    event?.preventDefault?.();
    enterPopoverVisible.value = true;
    return;
  }
  if (!["link", "jumpLink"].includes(action.responseType)) return;

  const safeLink = getSafeLink(action.link);
  if (!safeLink) return;
  event?.preventDefault?.();
  window.open(
    safeLink,
    action.responseType === "jumpLink" ? isTarget_Blank() : "_self",
  );
};

 

let { langJson, nowLocale, getLocaleText } = useLocate();
</script>
  
  <style scoped>
.input-item-wrap {
  height: 100%;
  
  
}
.input-item{
  height: 100%;
  
}

.input-item :deep(.el-input__inner){
  height: 100%;
}
</style>
  
